import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  AlertTriangle, 
  RefreshCw, 
  CheckCircle, 
  XCircle,
  Calendar,
  ClipboardList,
  UserCheck,
  BookOpen,
  FileText,
  Loader2
} from 'lucide-react';
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminResetPage() {
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [resetOptions, setResetOptions] = useState({
    attendance: true,
    grades: true,
    publishStatus: true
  });
  const [isResetting, setIsResetting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [resetResult, setResetResult] = useState(null);

  // Get user's school type to determine available year levels
  const user = JSON.parse(localStorage.getItem('user') || localStorage.getItem('currentUser') || '{}');
  const schoolType = user?.school_type || '';
  const schoolId = user?.school_id || '';
  
  // Determine year levels based on school type
  const getYearLevels = () => {
    // Secondary schools (St Patrick's College, Junior St Patrick's, etc.)
    if (schoolType === 'senior_secondary' || schoolType === 'junior_secondary' || 
        schoolId?.includes('stpatricks') || schoolId?.includes('tagaga') ||
        schoolId?.includes('ambaebulu-jss')) {
      return [7, 8, 9, 10, 11, 12, 13];
    }
    // Primary schools
    if (schoolType === 'primary' || schoolId?.includes('primary')) {
      return [1, 2, 3, 4, 5, 6];
    }
    // ECCE/Kindy
    if (schoolType === 'ecce' || schoolId?.includes('kindy')) {
      return [1, 2];
    }
    // Default - show all years
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
  };
  
  const yearLevels = getYearLevels();

  const handleResetTermData = async () => {
    if (!selectedTerm) {
      toast.error('Please select a term to reset');
      return;
    }

    if (!resetOptions.attendance && !resetOptions.grades && !resetOptions.publishStatus) {
      toast.error('Please select at least one data type to reset');
      return;
    }

    setShowConfirmDialog(true);
  };

  const confirmReset = async () => {
    if (confirmText !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }

    setIsResetting(true);
    setShowConfirmDialog(false);

    try {
      const API_URL = '';
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_URL}/api/admin/reset-term`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          year: selectedYear,
          term: selectedTerm,
          resetAttendance: resetOptions.attendance,
          resetGrades: resetOptions.grades,
          resetPublishStatus: resetOptions.publishStatus
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResetResult({
          success: true,
          message: data.message,
          details: data.reset_count
        });
        toast.success(data.message);
      } else {
        setResetResult({
          success: false,
          message: data.detail || 'Failed to reset term data'
        });
        toast.error(data.detail || 'Failed to reset term data');
      }
    } catch (error) {
      console.error('Reset error:', error);
      setResetResult({
        success: false,
        message: 'Network error - please try again'
      });
      toast.error('Network error - please try again');
    } finally {
      setIsResetting(false);
      setConfirmText('');
    }
  };

  const getYearLabel = () => {
    if (selectedYear === 'all') return 'All Years';
    return `Year ${selectedYear}`;
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Term Reset Management</h1>
        <p className="text-gray-600">
          Reset grades, attendance, and report data for a new term. This action is typically performed at the beginning of each term.
        </p>
      </div>

      {/* Warning Alert */}
      <Alert className="mb-6 border-red-300 bg-red-50">
        <AlertTriangle className="h-5 w-5 text-red-600" />
        <AlertDescription className="text-red-800 font-medium">
          <strong>CAUTION:</strong> These actions are irreversible. All selected data will be permanently deleted.
          Ensure you have backed up any important data before proceeding.
        </AlertDescription>
      </Alert>

      {/* Reset Result */}
      {resetResult && (
        <Alert className={`mb-6 ${resetResult.success ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
          {resetResult.success ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600" />
          )}
          <AlertDescription className={resetResult.success ? 'text-green-800' : 'text-red-800'}>
            {resetResult.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Main Reset Card */}
      <Card className="border-2 border-orange-200">
        <CardHeader className="bg-orange-50">
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <RefreshCw className="h-6 w-6" />
            Reset Term Data
          </CardTitle>
          <CardDescription className="text-orange-700">
            Select the term and data types you want to reset
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          {/* Term Selection */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Select Term</Label>
              <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                <SelectTrigger className="w-full" data-testid="term-select">
                  <SelectValue placeholder="Choose term..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Term 1">Term 1</SelectItem>
                  <SelectItem value="Term 2">Term 2</SelectItem>
                  <SelectItem value="Term 3">Term 3</SelectItem>
                  <SelectItem value="Term 4">Term 4</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Year Level (Optional)</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-full" data-testid="year-select">
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {yearLevels.map(year => (
                    <SelectItem key={year} value={String(year)}>Year {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Data Type Selection */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">Data to Reset</Label>
            
            <div className="grid gap-4 md:grid-cols-3">
              {/* Attendance */}
              <Card className={`cursor-pointer transition-all ${resetOptions.attendance ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                    onClick={() => setResetOptions(prev => ({ ...prev, attendance: !prev.attendance }))}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      checked={resetOptions.attendance}
                      onCheckedChange={(checked) => setResetOptions(prev => ({ ...prev, attendance: checked }))}
                      data-testid="reset-attendance-checkbox"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-sm">Attendance</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Roll call records & attendance statistics</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Grades */}
              <Card className={`cursor-pointer transition-all ${resetOptions.grades ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                    onClick={() => setResetOptions(prev => ({ ...prev, grades: !prev.grades }))}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      checked={resetOptions.grades}
                      onCheckedChange={(checked) => setResetOptions(prev => ({ ...prev, grades: checked }))}
                      data-testid="reset-grades-checkbox"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <ClipboardList className="h-4 w-4 text-green-600" />
                        <span className="font-medium text-sm">Grades & Marks</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">All student marks & assessment scores</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Publish Status */}
              <Card className={`cursor-pointer transition-all ${resetOptions.publishStatus ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}
                    onClick={() => setResetOptions(prev => ({ ...prev, publishStatus: !prev.publishStatus }))}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      checked={resetOptions.publishStatus}
                      onCheckedChange={(checked) => setResetOptions(prev => ({ ...prev, publishStatus: checked }))}
                      data-testid="reset-publish-checkbox"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-purple-600" />
                        <span className="font-medium text-sm">Report Status</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Report publication records</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Summary */}
          {selectedTerm && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Reset Summary</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• <strong>Term:</strong> {selectedTerm}</li>
                <li>• <strong>Year Level:</strong> {getYearLabel()}</li>
                <li>• <strong>Data to delete:</strong> {[
                  resetOptions.attendance && 'Attendance',
                  resetOptions.grades && 'Grades/Marks',
                  resetOptions.publishStatus && 'Report Status'
                ].filter(Boolean).join(', ') || 'None selected'}</li>
              </ul>
            </div>
          )}

          {/* Reset Button */}
          <Button 
            onClick={handleResetTermData}
            disabled={!selectedTerm || isResetting || (!resetOptions.attendance && !resetOptions.grades && !resetOptions.publishStatus)}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            size="lg"
            data-testid="reset-term-button"
          >
            {isResetting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Resetting Data...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset {selectedTerm || 'Term'} Data
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Usage Guidelines */}
      <Card className="mt-6 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            When to Use This Tool
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-gray-700">
            <div>
              <strong className="text-blue-800">Beginning of New Term:</strong>
              <p className="text-gray-600">Reset grades, marks, and attendance to start fresh. Teachers will enter new marks for the new term.</p>
            </div>
            <div>
              <strong className="text-blue-800">End of Academic Year:</strong>
              <p className="text-gray-600">Reset all terms to prepare for the new academic year. Ensure all report cards have been downloaded first.</p>
            </div>
            <div>
              <strong className="text-blue-800">Data Correction:</strong>
              <p className="text-gray-600">If incorrect data was entered, reset and have teachers re-enter the correct information.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Confirm Data Reset
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              This action will permanently delete the following data:
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
            <ul className="space-y-1 text-sm text-red-800">
              <li><strong>Term:</strong> {selectedTerm}</li>
              <li><strong>Year Level:</strong> {getYearLabel()}</li>
              {resetOptions.attendance && <li>✓ All attendance records</li>}
              {resetOptions.grades && <li>✓ All grades and marks</li>}
              {resetOptions.publishStatus && <li>✓ Report publication status</li>}
            </ul>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Type <span className="text-red-600 font-bold">RESET</span> to confirm:</Label>
            <Input 
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="Type RESET"
              className="text-center font-mono text-lg"
              data-testid="confirm-reset-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowConfirmDialog(false);
              setConfirmText('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={confirmReset}
              disabled={confirmText !== 'RESET'}
              data-testid="confirm-reset-button"
            >
              Confirm Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
